import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useReducer, useRef, useState, } from 'react';
import { FocusRing } from '@react-aria/focus';
import classNames from 'classnames';
import { PureButton } from '../../basics';
import { CloseIcon, HelperText } from '../../components';
import { InputSizes, KeyEvent, ValidationStates } from '../../enums';
import { Input } from '../../form';
import { useClickOutside } from '../../hooks';
import { createDefaultTypeaheadReducerState, defaultSuggestion, getHighlightedSuggestionIndex, TypeaheadActionType, typeaheadReducer, } from '../../reducer';
import { SuggestionList } from './SuggestionList';
import styles from './Typeahead.module.scss';
const Typeahead = ({ a11yCloseButton, dataTestId, formClasses, groupComponent, helperText, helperTextClasses, hideLabel = false, inputValue, onInputChange, onInputClear, onSelect, placeholder, poweredByImage, showHelperText, startAdornment, suggestionData, validationState = ValidationStates.Success, }) => {
    const [typeaheadState, dispatchTypeaheadState] = useReducer(typeaheadReducer, {
        suggestions: suggestionData,
        selectedSuggestion: defaultSuggestion,
    }, createDefaultTypeaheadReducerState);
    const { selectedSuggestion, suggestions } = typeaheadState;
    const [showSuggestionList, setShowSuggestionList] = useState(false);
    const formRef = useRef(null);
    const highlightedIndex = getHighlightedSuggestionIndex(suggestions);
    const helperId = useId();
    useEffect(() => {
        if (suggestionData) {
            dispatchTypeaheadState({
                type: TypeaheadActionType.Suggestions,
                suggestions: suggestionData,
            });
        }
    }, [suggestionData]);
    useClickOutside(formRef, () => {
        setShowSuggestionList(false);
    });
    const handleInputChange = (e) => {
        if (!showSuggestionList) {
            setShowSuggestionList(true);
        }
        dispatchTypeaheadState({
            type: TypeaheadActionType.Selected,
            selectedSuggestion: {
                ...selectedSuggestion,
                title: e.target.value,
            },
        });
        onInputChange?.(e);
    };
    const handleClearInput = () => {
        dispatchTypeaheadState({
            type: TypeaheadActionType.Reset,
        });
        onInputClear?.();
    };
    const handleSuggestionSelection = (suggestion) => {
        dispatchTypeaheadState({
            type: TypeaheadActionType.Selected,
            selectedSuggestion: suggestion,
        });
        setShowSuggestionList(false);
        onSelect?.(suggestion);
    };
    const handleKeyDown = event => {
        const suggestion = suggestions[highlightedIndex];
        switch (event.code) {
            case KeyEvent.ArrowUp:
                event.preventDefault();
                dispatchTypeaheadState({
                    type: TypeaheadActionType.Suggestions,
                    suggestions: suggestions.map((item, index) => ({
                        ...item,
                        highlighted: highlightedIndex > 0
                            ? highlightedIndex - 1 === index
                            : suggestions.length - 1 === index,
                    })),
                });
                break;
            case KeyEvent.ArrowDown:
                dispatchTypeaheadState({
                    type: TypeaheadActionType.Suggestions,
                    suggestions: suggestions.map((item, index) => ({
                        ...item,
                        highlighted: highlightedIndex < suggestions.length - 1
                            ? highlightedIndex + 1 === index
                            : index === 0,
                    })),
                });
                break;
            case KeyEvent.Enter:
                event.preventDefault();
                if (suggestion) {
                    handleSuggestionSelection(suggestion);
                }
                break;
            default:
                setShowSuggestionList(false);
                break;
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("form", { "data-testid": dataTestId, autoComplete: "off", className: classNames('d-flex position-relative flex-fill w-100', formClasses), ref: formRef, children: [_jsx(FocusRing, { focusClass: styles.focus, focusRingClass: styles.focusRing, within: true, children: _jsxs("div", { className: classNames('d-flex w-100', styles.wrapper), children: [_jsx(Input, { className: "w-100", inputSize: InputSizes.Standard, 
                                    // name attribute requires to include "search" for safari to stop autofill
                                    name: "typeaheadSearch", onClick: () => setShowSuggestionList(!showSuggestionList), onInput: handleInputChange, onKeyDown: handleKeyDown, placeholder: placeholder, value: inputValue, hideLabel: hideLabel, inputClasses: styles.input, "aria-label": placeholder, leftComponent: startAdornment, hasRightGroup: !!groupComponent, disabledFocusState: true, "aria-describedby": showHelperText && helperText ? helperId : undefined, rightComponent: (!!inputValue && (_jsxs(PureButton, { "aria-label": a11yCloseButton, onClick: handleClearInput, className: "d-flex", children: [_jsx("span", { className: "screenReaderOnly", children: a11yCloseButton }), _jsx(CloseIcon, {})] }))) ||
                                        null }), groupComponent || null] }) }), suggestions.length > 0 && showSuggestionList && (_jsx(SuggestionList, { selectedIndex: highlightedIndex, dispatch: dispatchTypeaheadState, suggestions: suggestions, onSuggestionClick: handleSuggestionSelection, poweredByImage: poweredByImage }))] }), showHelperText && helperText && (_jsx(HelperText, { id: helperId, className: helperTextClasses, validationState: validationState, children: helperText }))] }));
};
Typeahead.displayName = Typeahead.name;
export { Typeahead };
